import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import AuthField from "@/components/AuthField";
import AuthShell, { AUTH_BUTTON } from "@/components/AuthShell";
import { forgotPassword, resetPassword } from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

// Two-step reset: (1) submit the email to receive a 6-digit code; (2) submit the code and a new password.
type Step = "request" | "reset";

// Seconds before another code can be requested (the backend keeps only one live OTP per email).
const RESEND_COOLDOWN = 30;

export default function ForgotPasswordPage() {
  useDocumentTitle("Reset password · SkillPatch");
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("request");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Count the resend cooldown down to zero.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  async function sendCode(target: string) {
    await forgotPassword(target);
    setCooldown(RESEND_COOLDOWN);
  }

  async function onRequest(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await sendCode(email);
      setStep("reset");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  async function onResend() {
    if (cooldown > 0 || loading) return;
    setError(null);
    try {
      await sendCode(email);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't resend the code");
    }
  }

  async function onReset(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await resetPassword(email, otp.trim(), password);
      // Replace so Back doesn't return to the (now-consumed) reset form.
      navigate("/login?reset=1", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't reset password");
    } finally {
      setLoading(false);
    }
  }

  const footer = (
    <>
      Remembered it?{" "}
      <Link to="/login" className="font-medium text-accent hover:underline">
        Back to log in
      </Link>
    </>
  );

  if (step === "request") {
    return (
      <AuthShell
        title="Forgot password?"
        subtitle="Enter your email and we'll send you a reset code."
        footer={footer}
      >
        <form onSubmit={onRequest} className="flex flex-col gap-5">
          <AuthField
            icon="mail"
            type="email"
            required
            autoFocus
            autoComplete="email"
            placeholder="Email address"
            value={email}
            onChange={setEmail}
          />
          {error && <p className="text-sm text-red-400">{error}</p>}
          <button type="submit" disabled={loading} className={`mt-1 ${AUTH_BUTTON}`}>
            {loading ? "Sending…" : "Send reset code"}
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle={`Enter the verification code sent to ${email}`}
      footer={footer}
    >
      <form onSubmit={onReset} className="flex flex-col gap-5">
        <AuthField
          icon="key"
          type="text"
          required
          autoFocus
          name="otp"
          autoComplete="one-time-code"
          inputMode="numeric"
          maxLength={6}
          placeholder="6-digit code"
          value={otp}
          onChange={(v) => setOtp(v.replace(/\D/g, ""))}
        />
        <AuthField
          icon="lock"
          type="password"
          required
          minLength={8}
          autoComplete="new-password"
          placeholder="New password (min 8 characters)"
          value={password}
          onChange={setPassword}
        />
        <AuthField
          icon="lock"
          type="password"
          required
          minLength={8}
          autoComplete="new-password"
          placeholder="Confirm new password"
          value={confirm}
          onChange={setConfirm}
          invalid={confirm.length > 0 && confirm !== password}
        />
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button
          type="submit"
          disabled={loading || otp.length < 6 || password.length < 8 || password !== confirm}
          className={`mt-1 ${AUTH_BUTTON} disabled:opacity-60`}
        >
          {loading ? "Updating…" : "Reset Password"}
        </button>
        <p className="text-center text-sm" style={{ color: "var(--text-muted)" }}>
          Didn&apos;t receive the code?{" "}
          {cooldown > 0 ? (
            <span>Resend in {cooldown}s</span>
          ) : (
            <button type="button" onClick={onResend} className="font-medium text-accent hover:underline">
              Resend code
            </button>
          )}
        </p>
      </form>
    </AuthShell>
  );
}
